import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { roleLabel, useMemberships, usePersons, useRoles, useRpc, type AdminPerson, type PersonView, type RoleRow } from '../data/people.ts'
import { dayText, filterStaff, groupStaff, periodsOf, staffEntries, STAFF_FILTERS, titleText, useEmployments, useJobTitles, type StaffEntry, type StaffFilter } from '../data/staff.ts'
import { localDate } from '../lib/time.ts'
import { Banner, Field, NotAllowed, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import { StepUpField, useSensitive } from '../ui/sensitive.tsx'
import { PersonAccess, ResetCodeBlock } from './PersonAccess.tsx'
import { PersonPrivacy, isErased } from './PersonPrivacy.tsx'
import { PersonFileSection } from './people/PersonFile.tsx'
import { HireForm, StaffJob, type Hired } from './people/StaffJob.tsx'

/** Réglages › Employés, the staff register (GK-001; M20 decision 11): the site's people grouped by fonction, with their dates, and for each person their fonction and dates,
 *  their information sheet (fiche de renseignements), their access (roles, login, second factors, PIN) and their personal data (access export, erasure). Names,
 *  roles, fonctions and periods are in the device's mirror; the sheet, logins and factors are read from the server and changed online, after a fresh second-factor
 *  code. Removing an employee is a departure (person.depart@1), never a delete; the erasure (M11) stays a separate step. */
export function PeoplePage() {
  const { t, i18n } = useTranslation()
  const { org, site } = useClub()
  const persons = usePersons(); const memberships = useMemberships(); const roles = useRoles(); const titles = useJobTitles(); const employments = useEmployments()
  const create = useWriteAccess('person.manage')
  const member = useWriteAccess('member.manage')
  const pin = useWriteAccess('pin.reset')
  const access = useWriteAccess('export.run')          // M11: a person's access export (the direction)
  const contacts = useWriteAccess('contact.manage')     // M20: the information sheet and the emergency contacts
  const qualifications = useWriteAccess('qualification.read')
  // M23 item 24: the register is the office's (the same four permissions that show its row in Réglages); the jardinier who opens /people by URL gets the card
  const allowed = create.permitted || member.permitted || pin.permitted || qualifications.permitted
  const s = useSensitive()
  const admin = useRpc<AdminPerson[]>('admin_people', { p_org: org.org_id }, member.permitted)
  const [open, setOpen] = useState<string | null>(null)
  const [filter, setFilter] = useState<StaffFilter>('active')
  // M23 item 52: after "Employé ajouté.", the way to the new person's access (their row, opened on the Accès section)
  const [hired, setHired] = useState<Hired | null>(null)
  const [focusAccess, setFocusAccess] = useState<string | null>(null)
  useEffect(() => {
    if (!focusAccess) return
    const el = document.querySelector(`[data-person="${focusAccess}"] [data-testid="person-access-section"]`)
    if (el) { el.scrollIntoView?.({ block: 'start', behavior: 'smooth' }); setFocusAccess(null) }
  })
  const today = localDate(new Date(), site.timezone)
  const lang = i18n.language
  const entries = useMemo(() => (persons && memberships && titles && employments ? staffEntries(persons, employments, memberships, titles, site.site_id, today) : undefined),
    [persons, memberships, titles, employments, site.site_id, today])
  if (!allowed) return <NotAllowed permission="person.manage" />
  if (!entries || !roles || !titles || !employments) return <p>{t('common.loading')}</p>
  const info = new Map((admin.data ?? []).map((p) => [p.person_id, p]))
  const groups = groupStaff(filterStaff(entries, filter), titles)
  return (
    <section className="flex flex-col gap-5" data-testid="people-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('staff.title')}</h1>
        <div className="flex flex-wrap gap-2">
          {contacts.permitted && <Link to="/people/emergency" className="btn !min-h-11 !px-4" data-testid="go-emergency">{t('staff.emergency')}</Link>}
          {create.permitted && <Link to="/settings/job-titles" className="btn !min-h-11 !px-4" data-testid="go-job-titles">{t('staff.jobTitles')}</Link>}
          {member.permitted && <Link to="/devices" className="btn !min-h-11 !px-4" data-testid="go-devices">{t('people.devices')}</Link>}
          {qualifications.permitted && <Link to="/qualifications" className="btn !min-h-11 !px-4" data-testid="go-qualifications">{t('people.qualifications')}</Link>}
        </div>
      </div>
      <p className="meta">{t('staff.help')}</p>
      {member.allowed && <HireForm titles={titles} roles={roles} onDone={(h) => { if (h) setHired(h); void admin.reload() }} />}
      {hired && (
        <Banner tone="ok" testId="hire-access">
          <button type="button" className="btn w-full" data-testid="hire-access-go" onClick={() => { setOpen(hired.id); setFilter('active'); setFocusAccess(hired.id) }}>{t('staff.hire.giveAccess', { name: hired.name })}</button>
        </Banner>
      )}
      <div className="flex flex-wrap gap-2" role="group" aria-label={t('staff.filterLabel')}>
        {STAFF_FILTERS.map((f) => (
          <button key={f} className={`btn !px-4 ${filter === f ? 'btn-primary' : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)} data-testid="staff-filter" data-filter={f}>
            {t(`staff.filter.${f}`)} <span className="opacity-90">{filterStaff(entries, f).length}</span>
          </button>
        ))}
      </div>
      {groups.length === 0 && <p className="opacity-70" data-testid="staff-empty">{t('staff.empty')}</p>}
      {groups.map((g) => (
        <section key={g.key} className="flex flex-col gap-3" data-testid="staff-group" data-code={g.title?.code ?? 'none'}>
          <h2 className="text-lg font-bold">{g.title ? titleText(g.title.label, lang) : t('staff.noFonction')} <span className="font-normal opacity-60">{g.entries.length}</span></h2>
          {!g.title && <p className="meta -mt-2">{t('staff.noFonctionHelp')}</p>}
          <ul className="flex flex-col gap-3">
            {g.entries.map((e) => {
              const p = e.person as PersonView
              const a = info.get(p.id)
              // an erased person stays erased (M11): the server refuses any change of their profile, contacts, pay, roles, login or PIN (PERSON_ERASED), so none is offered
              const erased = isErased(p)
              const periods = periodsOf(employments, p.id, site.site_id)
              return (
                <li key={p.id} className="card flex flex-col gap-3" data-testid="person-row" data-person={p.id}>
                  <button className="flex min-h-11 flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpen(open === p.id ? null : p.id)} aria-expanded={open === p.id} data-testid="person-toggle">
                    <span className="text-lg font-bold">{p.display_name}</span>
                    <StaffPills entry={e} admin={a} pending={Boolean(p._pending)} lang={lang} roles={roles} />
                  </button>
                  {open === p.id && (
                    <div className="flex flex-col gap-4" data-testid="person-detail">
                      {erased && <p className="meta" data-testid="person-erased-note">{t('people.privacy.erasedNote')}</p>}
                      {create.allowed && !p._pending && !erased && <ProfileForm person={p} onSaved={() => void admin.reload()} />}
                      <StaffJob person={p} entry={e} periods={periods} titles={titles} roles={roles}
                        canHire={member.allowed && !p._pending} canPlan={create.allowed} erased={erased} onChanged={() => void admin.reload()} />
                      {/* the sheet is an employee's (its retention counts from the end of the last period; the server refuses it otherwise: PERSON_NOT_EMPLOYED) */}
                      {contacts.permitted && !p._pending && !erased && (periods.length > 0 ? <PersonFileSection personId={p.id} />
                        : <p className="meta border-t pt-4" data-testid="person-file-none">{t('personFile.notEmployed')}</p>)}
                      {(pin.permitted || member.permitted || access.permitted) && (
                        <div className="flex flex-col gap-4 border-t pt-4" data-testid="person-access-section">
                          {(pin.permitted || member.permitted) && <h3 className="text-lg font-bold">{t('staff.access')}</h3>}
                          <StepUpField s={s} />
                          {pin.permitted && !erased && <ResetCodeBlock personId={p.id} s={s} />}
                          {member.permitted && a && !erased && <PersonAccess person={a} roles={roles} s={s} onChanged={() => void admin.reload()} />}
                          {member.permitted && !a && admin.error && <Banner tone="error">{admin.error}</Banner>}
                        </div>
                      )}
                      {!p._pending && (member.permitted || access.permitted) && <PersonPrivacy person={p} s={s} canExport={access.permitted} canErase={member.permitted} onErased={() => void admin.reload()} />}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      ))}
    </section>
  )
}

/** What a row says at a glance: the fonction, the planned end of a saisonnier or an intern, a departure to come or past, a first day to come; for access without a
 *  fonction, the roles. */
function StaffPills({ entry: e, admin, pending, lang, roles }: { entry: StaffEntry; admin: AdminPerson | undefined; pending: boolean; lang: string; roles: RoleRow[] }) {
  const { t } = useTranslation()
  return (
    <span className="flex flex-wrap gap-1">
      {e.title && <span data-testid="person-fonction"><Pill tone={e.state === 'left' ? 'gray' : 'green'}>{titleText(e.title.label, lang)}</Pill></span>}
      {!e.title && e.roles.length === 0 && e.state === 'active' && <Pill tone="gray">{t('people.noRole')}</Pill>}
      {!e.title && e.roles.map((r) => <Pill key={r} tone="green">{roleLabel(roles, r, lang)}</Pill>)}
      {e.starts && <span data-testid="person-starts"><Pill tone="gray">{t('staff.pill.starts', { day: dayText(e.starts) })}</Pill></span>}
      {e.plannedEnd && <span data-testid="person-planned"><Pill tone="amber">{t('staff.pill.planned', { day: dayText(e.plannedEnd) })}</Pill></span>}
      {e.leaves && <span data-testid="person-leaves"><Pill tone="amber">{t('staff.pill.leaves', { day: dayText(e.leaves) })}</Pill></span>}
      {e.left && <span data-testid="person-left"><Pill tone="gray">{t('staff.pill.left', { day: dayText(e.left) })}</Pill></span>}
      {e.state === 'left' && !e.left && <Pill tone="gray">{t('staff.pill.ended')}</Pill>}
      {admin && e.state === 'active' && <Pill tone={admin.has_login ? 'gray' : 'amber'}>{admin.has_login ? t('people.hasLogin') : t('people.noLogin')}</Pill>}
      {pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
    </span>
  )
}

function ProfileForm({ person, onSaved }: { person: PersonView; onSaved: () => void }) {
  const { t } = useTranslation()
  const { busy, error, ok, pending, run } = useWorkshopAction()
  const [name, setName] = useState(person.display_name)
  const [locale, setLocale] = useState(person.preferred_locale)
  const changed = name.trim() !== person.display_name || locale !== person.preferred_locale
  return (
    <form className="flex flex-col gap-3" data-testid="profile-form" onSubmit={(e) => {
      e.preventDefault()
      const set: { display_name?: string; preferred_locale?: 'fr' | 'en' } = {}
      if (name.trim() !== person.display_name) set.display_name = name.trim()
      if (locale !== person.preferred_locale) set.preferred_locale = locale
      void run('person.update_profile@1', { person_id: person.id, expected_revision: person.revision ?? 1, set }, 'people.saved', { aggregateId: person.id }).then((done) => { if (done) onSaved() })
    }}>
      <Field label={t('people.name')} htmlFor={`pn-${person.id}`}><input id={`pn-${person.id}`} className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></Field>
      <Field label={t('people.language')} htmlFor={`pl-${person.id}`}>
        <select id={`pl-${person.id}`} className="input" value={locale} onChange={(e) => setLocale(e.target.value as 'fr' | 'en')}><option value="fr">Français</option><option value="en">English</option></select>
      </Field>
      {ok && <Banner tone="ok">{ok}</Banner>}
      {pending && <PendingBanner />}
      {error && <Banner tone="error">{error}</Banner>}
      <button className="btn" disabled={busy || !changed || !name.trim()} data-testid="profile-save">{t('common.save')}</button>
    </form>
  )
}
